import React, { useState, useEffect } from "react";
import { Container, Card, Badge, Spinner, Button, Row, Col, ProgressBar } from "react-bootstrap";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "../ThemeContext";
import { ArrowLeft, CircleFill, BarChartFill, Search, ShieldExclamation, JournalCheck } from "react-bootstrap-icons";
import "./DomainScanner.css"; // We keep this for any residual styles
import { apifetch } from '../services/interceptors';
import { riskBandFor, bandStyleScore } from './report/shapeReport';
const ExternalRiskReportPage = () => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileUrl = location.state?.fileUrl || searchParams.get("fileUrl");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    if (!fileUrl) {
      setError("No report file provided. Please go back and select a scan to view.");
      setLoading(false);
      return;
    }

    const fetchReportData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await apifetch("/s2/api/external_risk_data", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ fileUrl }),
        });

        if (!response.ok) throw new Error("Failed to fetch scan data");

        const data = await response.json();
        setReportData(data.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [fileUrl]);

  // Design tokens based exactly on screenshots
  const colors = {
    critical: "#ef4444", // Pipsqueak red
    high: "#f59e0b", // Pipsqueak orange
    medium: "#f59e0b", // Pipsqueak orange
    low: "#10b981", // Pipsqueak green
    passed: "#10b981", // Pipsqueak green
    purple: "#8b5cf6", // Pipsqueak primary
    border: isDarkMode ? "#444" : "#e2e8f0",
    cardBg: isDarkMode ? "#1e293b" : "#ffffff",
    textMain: isDarkMode ? "#f8f9fa" : "#0f172a",
    textMuted: isDarkMode ? "#94a3b8" : "#64748b",
  };

  const getSeverityColor = (severity) => {
    const s = String(severity).toLowerCase();
    if (s === "critical") return colors.critical;
    if (s === "high") return colors.high;
    if (s === "medium") return colors.medium;
    if (s === "low") return colors.low;
    return colors.passed; // default / passed
  };

  const getRiskLevelAndColor = (score) => {
    // Higher score = better security. 75-100: Low, 60-74: Medium, 40-59: High, 0-39: Critical
    if (score >= 75) return { label: "LOW RISK", color: colors.passed, bg: "success", badgeText: "white" };
    if (score >= 60) return { label: "MEDIUM RISK", color: colors.medium, bg: "warning", badgeText: "dark" };
    if (score >= 40) return { label: "HIGH RISK", color: colors.high, bg: "danger", badgeText: "white" };
    return { label: "CRITICAL RISK", color: colors.critical, bg: "danger", badgeText: "white" };
  };

  const getSecurityGrade = (score) => {
    if (score >= 90) return "A+";
    if (score >= 80) return "A";
    if (score >= 75) return "B+";
    if (score >= 70) return "B";
    if (score >= 60) return "C";
    if (score >= 40) return "D";
    return "F";
  };

  if (loading) {
    return (
      <Container className="py-5 text-center" style={{ minHeight: "100vh" }}>
        <Spinner animation="border" role="status" className="mb-3" />
        <h5 className={isDarkMode ? "text-light" : ""}>Loading External Risk Report...</h5>
      </Container>
    );
  }

  if (error) {
     return (
        <Container className="py-5 text-center" style={{ minHeight: "100vh" }}>
            <Button variant={isDarkMode ? "outline-light" : "outline-dark"} className="mb-4" onClick={() => navigate(-1)}>
               <ArrowLeft className="me-2" /> Back
            </Button>
            <div className={`p-4 rounded ${isDarkMode ? "bg-dark text-danger" : "bg-light text-danger"}`}>
                <ShieldExclamation size={48} className="mb-3" />
                <h5>{error}</h5>
            </div>
        </Container>
     )
  }

  if (!reportData) return null;

  // Calculate metrics
  let criticalCount = 0;
  let highCount = 0;
  let mediumCount = 0;
  let lowCount = 0;
  let totalIssues = 0;

  (reportData.categories || []).forEach(cat => {
    (cat.findings || []).forEach(f => {
      totalIssues++;
      const s = String(f.severity).toLowerCase();
      if (s === "critical") criticalCount++;
      else if (s === "high") highCount++;
      else if (s === "medium") mediumCount++;
      else if (s === "low") lowCount++;
    });
  });

  // Style tier follows the clamped band (see shapeReport.riskBandFor).
  const { label: riskLabel, color: riskColor, bg: riskBg, badgeText } =
    getRiskLevelAndColor(bandStyleScore(riskBandFor(reportData)));
  const grade = getSecurityGrade(reportData.riskScore);
  const categoryCount = reportData.categories?.length || 0;

  return (
    <Container fluid className={`py-4 ${isDarkMode ? "bg-dark" : "bg-light"}`} style={{ minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
      <Container style={{ maxWidth: "1000px" }}>
        
        <div className="d-flex align-items-center mb-4">
          <Button
            variant="link"
            className={`text-decoration-none p-0 d-flex align-items-center ${isDarkMode ? "text-light" : "text-dark"}`}
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="me-2" size={20} />
            <span className="fw-semibold pb-1">Back</span>  
          </Button>
        </div>

        {/* Top Summary Card */}
        <Card className="mb-4 border-0 shadow-sm" style={{ backgroundColor: colors.cardBg, borderRadius: "12px", overflow: "hidden" }}>
          <div style={{ width: "4px", backgroundColor: riskColor, position: "absolute", left: 0, top: 0, bottom: 0 }} />
          <Card.Body className="p-4 p-md-5 ps-5">
            <Row>
              <Col md={8}>
                <div className="d-flex align-items-center mb-1">
                  <div className="me-3 bg-white p-1 rounded shadow-sm d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, border: "1px solid #e0e0e0" }}>
                     <img 
                        src={`https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${reportData.domain?.replace(/^https?:\/\//, '').split('/')[0]}&size=128`}
                        alt="Logo" 
                        style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "10%" }}
                        onError={(e) => {
                           e.target.style.display = 'none';
                           e.target.nextSibling.style.display = 'flex';
                        }}
                     />
                     <div style={{ display: 'none', width: "100%", height: "100%", alignItems: "center", justifyContent: "center", backgroundColor: "#0d6efd", color: "#fff", borderRadius: "8px", fontWeight: "bold" }}>
                        {reportData.domain?.[0]?.toUpperCase() || 'S'}
                     </div>
                  </div>
                  <div>
                    <h5 className="mb-0 fw-bold" style={{ color: colors.textMain }}>{reportData.domain || "Unknown Domain"}</h5>
                    <small className="text-muted">Scanned just now</small>
                  </div>
                </div>
                
                <div className="mt-4 d-flex align-items-baseline">
                   <span className="fw-bold" style={{ fontSize: "5rem", color: colors.textMain, lineHeight: 1 }}>{reportData.riskScore}</span>
                   <span className="ms-1 fw-bold fs-3 text-muted"> /100</span>
                </div>

                <ProgressBar 
                   now={reportData.riskScore} 
                   className="mt-3 mb-4" 
                   style={{ height: "6px", backgroundColor: isDarkMode ? "#444" : "#e9ecef" }} 
                >
                    <ProgressBar 
                        now={reportData.riskScore} 
                        style={{ backgroundColor: riskColor }} 
                    />
                </ProgressBar>

                <div className="d-flex gap-4 flex-wrap mt-2">
                   <div className="d-flex align-items-center">
                     <CircleFill color={colors.critical} size={10} className="me-2" />
                     <span className="text-muted small">Critical: {criticalCount}</span>
                   </div>
                   <div className="d-flex align-items-center">
                     <CircleFill color={colors.high} size={10} className="me-2" />
                     <span className="text-muted small">High: {highCount}</span>
                   </div>
                   <div className="d-flex align-items-center">
                     <CircleFill color={colors.medium} size={10} className="me-2" />
                     <span className="text-muted small">Medium: {mediumCount}</span>
                   </div>
                   <div className="d-flex align-items-center">
                     <CircleFill color={colors.low} size={10} className="me-2" />
                     <span className="text-muted small">Low: {lowCount}</span>
                   </div>
                </div>
              </Col>
              
              <Col md={4} className="text-md-end mt-4 mt-md-0 d-flex flex-column align-items-md-end justify-content-start pt-2">
                 <Badge 
                   pill 
                   className="fs-6 py-2 px-3 border-0" 
                   style={{ 
                     backgroundColor: riskColor,
                     color: badgeText === "dark" ? "#000" : "#fff",
                     fontWeight: "700",
                   }}
                 >
                   {riskLabel}
                 </Badge>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* 4 Metrics Cards Grid */}
        <Row className="mb-5 g-3">
          <Col xs={6} md={3}>
             <Card className="h-100 border text-center py-4 shadow-sm" style={{ backgroundColor: colors.cardBg, borderColor: colors.border, borderRadius: "12px" }}>
                <BarChartFill size={28} className="mx-auto mb-2 text-primary" style={{ opacity: 0.8 }} />
                <h2 className="fw-bold mb-1" style={{ color: colors.purple, fontSize: "2.5rem" }}>{categoryCount}</h2>
                <small className="text-muted fw-semibold">Categories</small>
             </Card>
          </Col>
          <Col xs={6} md={3}>
             <Card className="h-100 border text-center py-4 shadow-sm" style={{ backgroundColor: colors.cardBg, borderColor: colors.border, borderRadius: "12px" }}>
                <Search size={28} className="mx-auto mb-2 text-secondary" style={{ opacity: 0.8 }} />
                <h2 className="fw-bold mb-1" style={{ color: colors.purple, fontSize: "2.5rem" }}>{totalIssues}</h2>
                <small className="text-muted fw-semibold">Total Issues</small>
             </Card>
          </Col>
          <Col xs={6} md={3}>
             <Card className="h-100 border text-center py-4 shadow-sm" style={{ backgroundColor: colors.cardBg, borderColor: colors.border, borderRadius: "12px" }}>
                <ShieldExclamation size={28} className="mx-auto mb-2 text-warning" style={{ opacity: 0.8 }} />
                <h2 className="fw-bold mb-1" style={{ color: colors.purple, fontSize: "2.5rem" }}>{criticalCount + highCount}</h2>
                <small className="text-muted fw-semibold">Critical/High</small>
             </Card>
          </Col>
          <Col xs={6} md={3}>
             <Card className="h-100 border text-center py-4 shadow-sm" style={{ backgroundColor: colors.cardBg, borderColor: colors.border, borderRadius: "12px" }}>
                <JournalCheck size={28} className="mx-auto mb-2 text-success" style={{ opacity: 0.8 }} />
                <h2 className="fw-bold mb-1" style={{ color: colors.purple, fontSize: "2.5rem" }}>{grade}</h2>
                <small className="text-muted fw-semibold">Security Grade</small>
             </Card>
          </Col>
        </Row>

        {/* Categories Section */}
        <h4 className="fw-bold mb-4" style={{ color: colors.textMain }}>Security Findings by Category</h4>
        
        {reportData.categories && reportData.categories.length > 0 ? (
          reportData.categories.map((category, idx) => (
            <Card key={idx} className="mb-4 border shadow-sm" style={{ backgroundColor: colors.cardBg, borderColor: colors.border, borderRadius: "12px" }}>
              <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-2 d-flex justify-content-between align-items-center">
                <h6 className="fw-bold mb-0 fs-5" style={{ color: colors.textMain }}>{category.name}</h6>
                <span className="text-muted small fw-semibold">{category.findings?.length || 0} findings</span>
              </Card.Header>
              
              <Card.Body className="px-4 pb-4 pt-2">
                {category.findings && category.findings.length > 0 ? (
                  <div className="d-flex flex-column gap-3 mt-2">
                    {category.findings.map((finding, fIdx) => (
                      <div 
                        key={fIdx} 
                        className="p-3 border rounded d-flex flex-column"
                        style={{ borderColor: colors.border, backgroundColor: isDarkMode ? "#333" : "#fff" }}
                      >
                         <div className="d-flex align-items-start">
                           <CircleFill 
                             color={getSeverityColor(finding.severity)} 
                             size={14} 
                             className="me-3 mt-1 flex-shrink-0" 
                           />
                           <div>
                             <h6 className="fw-bold mb-1" style={{ color: colors.textMain, fontSize: "0.95rem" }}>
                               {finding.title}
                             </h6>
                             <p className="mb-0 text-muted" style={{ fontSize: "0.85rem" }}>
                               {finding.description}
                             </p>
                           </div>
                         </div>
                         
                         {finding.value && (
                            <div className="ms-4 ps-1 mt-2">
                              <pre
                                className="p-2 rounded mb-0 text-muted border border-secondary"
                                style={{ 
                                  fontSize: "0.8rem", 
                                  whiteSpace: "pre-wrap", 
                                  wordBreak: "break-word",
                                  backgroundColor: isDarkMode ? "#222" : "#f8f9fa"
                                }}
                              >
                                {finding.value}
                              </pre>
                            </div>
                         )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted small py-2">No findings categorized under this section.</div>
                )}
              </Card.Body>
            </Card>
          ))
        ) : (
          <div className="text-center py-5">
             <h5 className="text-muted">No structured category data found in this report.</h5>
          </div>
        )}

      </Container>
    </Container>
  );
};

export default ExternalRiskReportPage;
